import { sanitizeAs400Text } from "./products.contract";

export const AS400_ITEM_NUMBER_MAX_LENGTH = 20;
export const AS400_VENDOR_CODE_LENGTH = 3;

const VENDOR_PART_KEEP = /[^A-Z0-9./]/g;
const VENDOR_CODE_KEEP = /[^A-Z]/g;
const ITEM_NUMBER_BODY = /^[A-Z0-9./]+$/;
const ITEM_NUMBER_TRUNCATED = /^([A-Z0-9./]+)\$(\d+)$/;

export type As400ItemNumberFormatProblem =
  "disallowed-character" | "truncation-marker";

export interface As400ItemNumberBuild {
  itemNumber: string;
  removed: number;
  removedText: string;
}

export interface As400ItemNumberTruncation {
  stem: string;
  removed: number;
}

export function cleanAs400VendorPart(vendorPart: string): string {
  return sanitizeAs400Text(vendorPart).replace(VENDOR_PART_KEEP, "");
}

export function cleanAs400VendorCode(vendorCode: string): string {
  return sanitizeAs400Text(vendorCode)
    .replace(VENDOR_CODE_KEEP, "")
    .slice(0, AS400_VENDOR_CODE_LENGTH);
}

export function parseAs400ItemNumberTruncation(
  itemNumber: string,
): As400ItemNumberTruncation | null {
  const match = ITEM_NUMBER_TRUNCATED.exec(itemNumber);
  const stem = match?.[1];
  const digits = match?.[2];
  if (stem === undefined || digits === undefined) return null;
  return { stem, removed: Number(digits) };
}

export function as400ItemNumberFormatProblem(
  value: string,
): As400ItemNumberFormatProblem | null {
  if (ITEM_NUMBER_BODY.test(value)) return null;
  if (ITEM_NUMBER_TRUNCATED.test(value)) return null;
  return value.includes("$") ? "truncation-marker" : "disallowed-character";
}

function truncatedItemNumber(
  prefix: string,
  kept: string,
  removed: number,
): string {
  return `${prefix}${kept}$${removed}`;
}

function firstFittingRemoved(prefix: string, cleaned: string): number {
  for (let removed = 1; removed <= cleaned.length; removed++) {
    const kept = cleaned.slice(0, cleaned.length - removed);
    if (
      truncatedItemNumber(prefix, kept, removed).length <=
      AS400_ITEM_NUMBER_MAX_LENGTH
    ) {
      return removed;
    }
  }
  return cleaned.length;
}

export function buildAs400ItemNumber(
  vendorCode: string,
  vendorPart: string,
): As400ItemNumberBuild {
  const prefix = cleanAs400VendorCode(vendorCode);
  const cleaned = cleanAs400VendorPart(vendorPart);
  const full = `${prefix}${cleaned}`;
  if (full.length <= AS400_ITEM_NUMBER_MAX_LENGTH) {
    return { itemNumber: full, removed: 0, removedText: "" };
  }
  const removed = firstFittingRemoved(prefix, cleaned);
  const kept = cleaned.slice(0, cleaned.length - removed);
  return {
    itemNumber: truncatedItemNumber(prefix, kept, removed),
    removed,
    removedText: cleaned.slice(cleaned.length - removed),
  };
}

export function suggestCollisionAdjustment(itemNumber: string): string | null {
  const parsed = parseAs400ItemNumberTruncation(itemNumber);
  const stem = parsed?.stem ?? itemNumber;
  const baseRemoved = parsed?.removed ?? 0;
  const maxExtra = stem.length - AS400_VENDOR_CODE_LENGTH;
  for (let extra = 1; extra <= maxExtra; extra++) {
    const suggestion = truncatedItemNumber(
      "",
      stem.slice(0, -extra),
      baseRemoved + extra,
    );
    if (
      suggestion.length <= AS400_ITEM_NUMBER_MAX_LENGTH &&
      as400ItemNumberFormatProblem(suggestion) === null
    ) {
      return suggestion;
    }
  }
  return null;
}
